import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchRegions, type RegionNode } from '../../../api/regions';
import {
  fetchCurrentStaff,
  fetchStaff,
  type CurrentStaff,
  type StaffMember,
  type UserManagementAccess,
} from '../../../api/staff';
import { topLevelRegionOptions, type RegionOption } from '../../../shared/regionAccessOptions';

import { toUserRow, type UserRow } from './userViewModel';

interface UsersPageData {
  loading: boolean;
  error: string | null;
  self: CurrentStaff | null;
  /** What the viewer may do on the page; nothing until the profile loads. */
  userManagement: UserManagementAccess;
  /** When the viewer only manages their own regions: the region ids they can
   *  assign, so other regions render locked. Null when unrestricted. */
  manageableRegionIds: string[] | null;
  /** Human-readable scope label, e.g. "Pacific Northwest". Null when unrestricted. */
  scopedRegionName: string | null;
  /** All rows from the API, in their original order. As of BON-52, the
   *  backend pre-filters by the caller's region scope, so this is already
   *  in-scope for the viewer. */
  allRows: UserRow[];
  /** Alias of `allRows`. Kept for backward compat with consumers that
   *  referenced the prior client-side-filter distinction. Identical to
   *  allRows now that the backend returns pre-scoped data. */
  inScopeRows: UserRow[];
  /** Flat list of every region in the tenant — used by the admin region filter. */
  regionOptions: RegionOption[];
  /** Re-fetch the staff list (e.g. after a PATCH from a dialog). */
  refetch: () => Promise<void>;
}

const NO_USER_MANAGEMENT: UserManagementAccess = {
  canInvite: false,
  grantableRoles: [],
  assignableRegionIds: [],
  confinedToRegions: false,
};

/** Loads the data the Users page needs (staff list + viewer profile) and
 *  derives the viewer's own scope + row set. Filtering and selection live
 *  in the page itself. */
export function useUsersPageData(): UsersPageData {
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [self, setSelf] = useState<CurrentStaff | null>(null);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts: { signal?: { cancelled: boolean } } = {}) => {
    setError(null);
    try {
      const [s, me, regions] = await Promise.all([
        fetchStaff(),
        fetchCurrentStaff(),
        fetchRegions().catch((): RegionNode[] => []),
      ]);
      if (opts.signal?.cancelled) return;
      setStaff(s);
      setSelf(me);
      setRegionOptions(topLevelRegionOptions(regions));
    } catch (err: unknown) {
      if (opts.signal?.cancelled) return;
      setError(err instanceof Error ? err.message : 'Failed to load users');
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void load({ signal });
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  const refetch = useCallback(async () => {
    await fetchStaff()
      .then((s) => setStaff(s))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to refresh users'),
      );
  }, []);

  const userManagement = self?.userManagement ?? NO_USER_MANAGEMENT;
  const confined = userManagement.confinedToRegions;

  const manageableRegionIds = confined ? userManagement.assignableRegionIds : null;

  const scopedRegionName = useMemo<string | null>(() => {
    if (!confined || !self || self.regions.length === 0) return null;
    return self.regions.map((r) => r.region.name).join(' · ');
  }, [confined, self]);

  // BON-52: the backend now applies region scoping server-side, so we no
  // longer filter on the client. The `inScopeRows` alias is preserved for
  // back-compat with consumers that reference it; both keys return the
  // same array. Future cleanup can collapse to a single `rows` key.
  const allRows: UserRow[] = useMemo(() => (staff ? staff.map(toUserRow) : []), [staff]);

  return {
    loading: !staff && !error,
    error,
    self,
    userManagement,
    manageableRegionIds,
    scopedRegionName,
    allRows,
    inScopeRows: allRows,
    regionOptions,
    refetch,
  };
}
